import React, { useMemo } from 'react';

/**
 * Multi-month Survey Statistic.
 * Each question = 1 table. Columns grouped by answer, sub-columns by month.
 * Rows = branches, last row = total of area. No chart.
 *
 * props:
 *  months: ["2026-01", "2026-02"]
 *  monthData: { [month]: { [branchID]: surveyArray } }
 *  branches: [{ branchID, branchName }]
 *  areaName: string
 */

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function monthLabel(ym) {
    const [y, m] = String(ym).split("-");
    const name = MONTH_NAMES[Number(m) - 1] || m;
    return `${name}-${String(y).slice(2)}`;
}

function norm(value) {
    if (value === undefined || value === null) return "";
    return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '').toLowerCase();
}

const empty = (v) => v === 0 || v === "" || v === null || v === undefined;

function byNumber(map, value, textRules) {
    if (empty(value)) return "";
    const n = Number(value);
    if (map[n]) return map[n];
    const t = norm(value);
    for (const [test, label] of textRules) {
        if (test(t)) return label;
    }
    return "";
}

const RESOLVERS = {
    q1: (v) => byNumber({ 1: "1 day", 2: "2-6 days", 3: "1-4 week", 4: "above 1 month" }, v, [
        [t => t.includes('1day'), "1 day"],
        [t => t.includes('2-6') || t.includes('2to6') || t.includes('2~6'), "2-6 days"],
        [t => t.includes('1-4week') || t.includes('1to4week') || t.includes('1~4week'), "1-4 week"],
        [t => t.includes('abovemonth') || t.includes('above1month') || t.includes('morethan1month'), "above 1 month"],
    ]),
    q2: (v) => byNumber({ 1: "secret", 2: "20's", 3: "30's", 4: "40's", 5: "50's", 6: "Above 50's" }, v, [
        [t => t.includes('secret'), "secret"],
        [t => t.includes('above50') || t.includes('over50') || t.includes('morethan50'), "Above 50's"],
        [t => t.includes('20'), "20's"],
        [t => t.includes('30'), "30's"],
        [t => t.includes('40'), "40's"],
        [t => t.includes('50'), "50's"],
    ]),
    q3: (v) => byNumber({ 1: "First time", 2: "Second time", 3: "Third time", 4: "More than" }, v, [
        [t => t.includes('first'), "First time"],
        [t => t.includes('second'), "Second time"],
        [t => t.includes('third'), "Third time"],
        [t => t.includes('more'), "More than"],
    ]),
    q4: (v) => byNumber({ 1: "Company", 2: "Introduction", 3: "AZ WEB", 4: "OTA", 5: "Google", 6: "SNS", 7: "POSTE", 8: "Magazine", 9: "Other" }, v, [
        [t => t.includes('company'), "Company"],
        [t => t.includes('intro'), "Introduction"],
        [t => t.includes('azweb') || t.includes('website'), "AZ WEB"],
        [t => t.includes('ota'), "OTA"],
        [t => t.includes('google'), "Google"],
        [t => t.includes('sns') || t.includes('facebook') || t.includes('instagram'), "SNS"],
        [t => t.includes('poste') || t.includes('poster'), "POSTE"],
        [t => t.includes('magazine'), "Magazine"],
        [t => t.includes('other'), "Other"],
    ]),
    q6: (v) => byNumber({ 1: 'Price', 2: 'Location', 3: 'Rotenburo', 4: 'Clealiness', 5: 'Breakfast', 6: 'Amenity', 7: 'Staff', 8: 'Japanese-chanel', 9: 'Internet', 10: 'Massage', 11: 'Japan-res', 12: 'Speak JP', 13: 'Other' }, v, [
        [t => t.includes('price') || t.includes('kyaria'), 'Price'],
        [t => t.includes('location') || t.includes('access') || t.includes('eki'), 'Location'],
        [t => t.includes('roten') || t.includes('onsen'), 'Rotenburo'],
        [t => t.includes('clean'), 'Clealiness'],
        [t => t.includes('breakfast') || t.includes('bf'), 'Breakfast'],
        [t => t.includes('amenity') || t.includes('facility'), 'Amenity'],
        [t => t.includes('staff') || t.includes('service'), 'Staff'],
        [t => t.includes('japanesechannel') || (t.includes('japanese') && t.includes('chanel')), 'Japanese-chanel'],
        [t => t.includes('internet') || t.includes('wifi') || t.includes('wi-fi'), 'Internet'],
        [t => t.includes('massage'), 'Massage'],
        [t => t.includes('japanres') || t.includes('restaurant'), 'Japan-res'],
        [t => t.includes('speakjp') || t.includes('speakjapanese'), 'Speak JP'],
        [t => t.includes('other'), 'Other'],
    ]),
    q7: (v) => byNumber({ 1: "Come again", 2: "Don't know", 3: "Don't come again" }, v, [
        [t => t.includes('comeagain'), "Come again"],
        [t => t.includes('dontknow') || t.includes('notsure'), "Don't know"],
        [t => t.includes('dontcome') || t.includes('notcome'), "Don't come again"],
    ]),
};

function splitQ6(q6) {
    if (Array.isArray(q6)) return q6;
    if (typeof q6 === 'string') {
        return q6.trim() === "" ? [] : q6.split(/[\n,、;；/|]+/).map(s => s.trim()).filter(Boolean);
    }
    if (q6 && typeof q6 === 'object') {
        return [q6.answer, q6.value, q6.name, q6.label, q6.text].filter(x => !empty(x));
    }
    return [];
}

const QUESTIONS = [
    { key: "q1", title: "1. How long guest staying", labels: ["1 day", "2-6 days", "1-4 week", "above 1 month"] },
    { key: "q2", title: "2. Guest Age", labels: ["secret", "20's", "30's", "40's", "50's", "Above 50's"] },
    { key: "q3", title: "3. How Many Times", labels: ["First time", "Second time", "Third time", "More than"] },
    { key: "q4", title: "4. How to Know Az", labels: ["Company", "Introduction", "AZ WEB", "OTA", "Google", "SNS", "POSTE", "Magazine", "Other"] },
    { key: "q5", title: "5. Score Survey", labels: ["Reception", "Azuman", "Kitchen", "HK", "Roten", "Massage"], isScore: true },
    { key: "q6", title: "6. Important Points", labels: ["Price", "Location", "Rotenburo", "Clealiness", "Breakfast", "Amenity", "Staff", "Japanese-chanel", "Internet", "Massage", "Japan-res", "Speak JP", "Other"] },
    { key: "q7", title: "7. Come Again", labels: ["Come again", "Don't know", "Don't come again"] },
];

const score = (q5, i) => {
    const v = Number(q5?.[i]?.answer || 0);
    return v > 0 ? v : 0;
};
const avg = (arr) => {
    const list = arr.filter(x => x > 0);
    return list.length ? list.reduce((a, b) => a + b, 0) / list.length : 0;
};

// -> { counts: {label: n}, total } for choice questions, { sums:{label:{sum,count}} } for score
function computeQuestion(question, surveys) {
    if (question.isScore) {
        const acc = {};
        question.labels.forEach(l => { acc[l] = { sum: 0, count: 0 }; });
        surveys.forEach(s => {
            const q5 = Array.isArray(s?.question5) ? s.question5 : [];
            const values = {
                Reception: score(q5, 1),
                Azuman: avg([score(q5, 2), score(q5, 7)]),
                Kitchen: avg([score(q5, 2), score(q5, 3), score(q5, 4), score(q5, 7)]),
                HK: avg([score(q5, 2), score(q5, 6), score(q5, 7)]),
                Roten: score(q5, 8),
                Massage: score(q5, 9),
            };
            Object.keys(values).forEach(l => {
                if (values[l] > 0) { acc[l].sum += values[l]; acc[l].count += 1; }
            });
        });
        return { score: acc };
    }

    const counts = {};
    question.labels.forEach(l => { counts[l] = 0; });
    let total = 0;
    surveys.forEach(s => {
        if (question.key === "q6") {
            let has = false;
            splitQ6(s?.question6).forEach(p => {
                const label = RESOLVERS.q6(p);
                if (label && label in counts) { counts[label] += 1; has = true; }
            });
            if (has) total += 1;
        } else {
            const label = RESOLVERS[question.key](s?.["question" + question.key.slice(1)]);
            if (label && label in counts) { counts[label] += 1; total += 1; }
        }
    });
    return { counts, total };
}

function formatCell(question, result, label) {
    if (question.isScore) {
        const item = result.score[label];
        return item.count === 0 ? "0" : (Math.round((item.sum / item.count) * 100) / 100).toString();
    }
    if (!result.total) return "0.00%";
    return `${((result.counts[label] * 100) / result.total).toFixed(2)}%`;
}

const cellStyle = { fontSize: '1.2rem', textAlign: 'center', whiteSpace: 'nowrap' };
const headStyle = { textAlign: 'center', background: '#70ad47', color: 'white', border: '1px solid #fff' };

function MultiMonthStatistic({ months = [], monthData = {}, branches = [], areaName = "Area" }) {
    const sortedMonths = useMemo(() => [...months].sort(), [months]);

    const computed = useMemo(() => {
        const result = {};
        QUESTIONS.forEach(q => {
            result[q.key] = { branch: {}, total: {} };
            sortedMonths.forEach(m => {
                const data = monthData[m] || {};
                let all = [];
                branches.forEach(b => {
                    const list = Array.isArray(data[b.branchID]) ? data[b.branchID] : [];
                    all = all.concat(list);
                    result[q.key].branch[`${b.branchID}_${m}`] = computeQuestion(q, list);
                });
                result[q.key].total[m] = computeQuestion(q, all);
            });
        });
        return result;
    }, [sortedMonths, monthData, branches]);

    if (sortedMonths.length === 0) return "";

    return (
        <div className="multi-month-statistic" style={{ overflowX: 'auto', paddingBottom: '24px' }}>
            <div style={{ marginTop: '24px' }}>
                <h4 style={{ marginBottom: '8px', marginLeft: "8px", fontWeight: 600, textAlign: 'left', color: "#e228d5", fontSize: "2rem" }}>Collected Surveys</h4>
                <table id="customers" style={{ margin: "0 12px" }}>
                    <thead>
                        <tr>
                            <th style={headStyle}>Area</th>
                            {sortedMonths.map(m => <th key={m} style={headStyle}>{monthLabel(m)}</th>)}
                            <th style={headStyle}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {branches.map(b => {
                            const counts = sortedMonths.map(m => (Array.isArray(monthData[m]?.[b.branchID]) ? monthData[m][b.branchID].length : 0));
                            return (
                                <tr key={b.branchID}>
                                    <td style={{ fontSize: '1.2rem', whiteSpace: 'nowrap' }}>{b.branchName}</td>
                                    {counts.map((c, i) => <td key={sortedMonths[i]} style={cellStyle}>{c}</td>)}
                                    <td style={{ ...cellStyle, fontWeight: 700 }}>{counts.reduce((a, c) => a + c, 0)}</td>
                                </tr>
                            );
                        })}
                        <tr style={{ background: '#fff200', fontWeight: 700 }}>
                            <td style={{ fontSize: '1.2rem', whiteSpace: 'nowrap' }}>{areaName}</td>
                            {(() => {
                                const totals = sortedMonths.map(m => branches.reduce((sum, b) => sum + (Array.isArray(monthData[m]?.[b.branchID]) ? monthData[m][b.branchID].length : 0), 0));
                                return (
                                    <>
                                        {totals.map((t, i) => <td key={sortedMonths[i]} style={cellStyle}>{t}</td>)}
                                        <td style={cellStyle}>{totals.reduce((a, t) => a + t, 0)}</td>
                                    </>
                                );
                            })()}
                        </tr>
                    </tbody>
                </table>
            </div>
            {QUESTIONS.map(q => (
                <div key={q.key} style={{ marginTop: '24px' }}>
                    <h4 style={{ marginBottom: '8px', marginLeft:"8px", fontWeight: 600, textAlign: 'left', color: "#e228d5", fontSize:"2rem" }}>{q.title}</h4>
                    <table id="customers" style={{margin:"0 12px" }}>
                        <thead>
                            <tr>
                                <th rowSpan={2} style={{ ...headStyle, verticalAlign: 'middle' }}>Area</th>
                                {q.labels.map(label => (
                                    <th key={label} colSpan={sortedMonths.length} style={headStyle}>{label}</th>
                                ))}
                            </tr>
                            <tr>
                                {q.labels.map(label => sortedMonths.map(m => (
                                    <th key={`${label}_${m}`} style={headStyle}>{monthLabel(m)}</th>
                                )))}
                            </tr>
                        </thead>
                        <tbody>
                            {branches.map(b => (
                                <tr key={b.branchID}>
                                    <td style={{ fontSize: '1.2rem', whiteSpace: 'nowrap' }}>{b.branchName}</td>
                                    {q.labels.map(label => sortedMonths.map(m => (
                                        <td key={`${b.branchID}_${label}_${m}`} style={cellStyle}>
                                            {formatCell(q, computed[q.key].branch[`${b.branchID}_${m}`], label)}
                                        </td>
                                    )))}
                                </tr>
                            ))}
                            <tr style={{ background: '#fff200', fontWeight: 700 }}>
                                <td style={{ fontSize: '1.2rem', whiteSpace: 'nowrap' }}>{areaName}</td>
                                {q.labels.map(label => sortedMonths.map(m => (
                                    <td key={`total_${label}_${m}`} style={cellStyle}>
                                        {formatCell(q, computed[q.key].total[m], label)}
                                    </td>
                                )))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

export default MultiMonthStatistic;
